import { Lazy } from '../decorators/lazy.js';

export type Observer<T> = (value: T) => void;

export function observableValue<T>(initialValue: T) {
  return new WritableObservableValue(initialValue);
}

export abstract class ObservableValue<T> {
  abstract get value(): T;

  protected observers: Set<Observer<T>> = new Set();

  protected notify() {
    this.observers.forEach(observer => observer(this.value));
  }

  subscribe(observer: Observer<T>): () => void {
    this.observers.add(observer);
    observer(this.value);
    return () => this.observers.delete(observer);
  }

  map<U>(transform: (_: T) => U): ObservableValue<U> {
    return new MappedObservableValue(transform, this);
  }
}

export class MappedObservableValue<T, U> extends ObservableValue<U> {
  private observedValue: ObservableValue<T>;
  private readonly transform: (_: T) => U;
  private currentInput?: T;
  private currentOutput?: U;

  constructor(transform: (_: T) => U, observedValue: ObservableValue<T>) {
    super();
    this.observedValue = observedValue;
    this.transform = transform;
    observedValue.subscribe(() => this.notify());
  }

  get value() {
    if (this.currentInput && this.observedValue.value === this.currentInput) {
      return this.currentOutput!;
    }
    this.currentInput = this.observedValue.value;
    this.currentOutput = this.transform(this.observedValue.value);
    return this.currentOutput;
  }
}

export class CombinedObservableValue extends ObservableValue<any> {
  private observedValues: ObservableValue<any>[];

  constructor(...observedValues: ObservableValue<any>[]) {
    super();
    this.observedValues = observedValues;
    observedValues.forEach(obs => obs.subscribe(() => this.notify()));
  }

  get value() {
    return this.observedValues.map(obs => obs.value);
  }
}

export function combine<T, U>(
  t: ObservableValue<T>,
  u: ObservableValue<U>
): ObservableValue<[T, U]>;
export function combine<T, U, V>(
  t: ObservableValue<T>,
  u: ObservableValue<U>,
  v: ObservableValue<V>
): ObservableValue<[T, U, V]>;
export function combine<T, U, V, W>(
  t: ObservableValue<T>,
  u: ObservableValue<U>,
  v: ObservableValue<V>,
  w: ObservableValue<W>
): ObservableValue<[T, U, V, W]>;
export function combine<T, U, V, W, X>(
  t: ObservableValue<T>,
  u: ObservableValue<U>,
  v: ObservableValue<V>,
  w: ObservableValue<W>,
  x: ObservableValue<X>
): ObservableValue<[T, U, V, W, X]>;
export function combine(
  ...o: ObservableValue<unknown>[]
): ObservableValue<unknown>;
export function combine(...o: ObservableValue<any>[]): ObservableValue<any> {
  return new CombinedObservableValue(...o);
}

export class WritableObservableValue<T> extends ObservableValue<T> {
  private _value: T;

  get value(): T {
    return this._value;
  }

  set value(newValue: T) {
    this._value = newValue;
    this.notify();
  }

  constructor(initialValue: T) {
    super();
    this._value = initialValue;
  }

  @Lazy()
  get asReadOnly(): ReadOnlyObservableValue<T> {
    return new ReadOnlyObservableValue(this);
  }
}

export class ReadOnlyObservableValue<T> extends ObservableValue<T> {
  private observedValue: ObservableValue<T>;

  constructor(observedValue: ObservableValue<T>) {
    super();
    this.observedValue = observedValue;
    this.observedValue.subscribe(() => this.notify());
  }

  get value() {
    return this.observedValue.value;
  }
}

function generateKey(name: string) {
  return `--${name.toLocaleLowerCase()}-observable-object`;
}

export abstract class ObservableObject {
  static key<T extends ObservableObject>(this: new () => T): string {
    return generateKey(this.name);
  }

  key() {
    return generateKey(this.constructor.name);
  }
}
